import { toast } from 'react-toastify';

import { getSerializedErrorMessage } from '../../../../utils/errors';
import { finishTreatmentPlanV3Mutation, startTreatmentPlanV3Mutation } from '../../utils';

import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import type { ITherapy } from '../../../../pages/common';
import type { TreatmentPlanV3PlanAction, TreatmentPlanV3State } from '../../types';
import type {
  ITreatmentPlanV3TherapyPage,
  CreateTreatmentPlanV3TherapyPayload,
  UpdateTreatmentPlanV3TherapyPayload,
  DeleteTreatmentPlanV3TherapyPayload,
  SetTreatmentPlanV3NoTherapyPayload,
  TreatmentPlanV3TherapyThunks,
  TherapyMutationAction,
} from './types';

export function addTreatmentPlanV3TherapyCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3TherapyThunks,
) {
  addGetTreatmentPlanV3TherapyPageCases(builder, thunks);
  addCreateTreatmentPlanV3TherapyCases(builder, thunks);
  addUpdateTreatmentPlanV3TherapyCases(builder, thunks);
  addDeleteTreatmentPlanV3TherapyCases(builder, thunks);
  addSetTreatmentPlanV3NoTherapyCases(builder, thunks);
}

function isForLoadedTherapyPage(state: TreatmentPlanV3State, action: TreatmentPlanV3PlanAction) {
  return action.meta.arg.treatmentPlanId === state.therapyPageTreatmentPlanId;
}

function addGetTreatmentPlanV3TherapyPageCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3TherapyThunks,
) {
  const { getTreatmentPlanV3TherapyPage } = thunks;

  builder
    .addCase(getTreatmentPlanV3TherapyPage.pending, (state, action) => {
      state.therapyPage = null;
      state.therapyPageLoading = true;
      state.therapyPageError = null;
      state.therapyPageRequestId = action.meta.requestId;
      state.therapyPageTreatmentPlanId = action.meta.arg.treatmentPlanId;
      state.error = null;
    })
    .addCase(getTreatmentPlanV3TherapyPage.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.therapyPageRequestId) return;
      state.therapyPageLoading = false;
      state.therapyPageError = null;
      state.therapyPage = action.payload;
    })
    .addCase(getTreatmentPlanV3TherapyPage.rejected, (state, action) => {
      if (action.meta.requestId !== state.therapyPageRequestId) return;
      state.therapyPageLoading = false;
      state.therapyPageError = action.error;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addCreateTreatmentPlanV3TherapyCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3TherapyThunks,
) {
  const { createTreatmentPlanV3Therapy } = thunks;
  const flag = 'createTherapyLoading';

  builder
    .addCase(createTreatmentPlanV3Therapy.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      createTreatmentPlanV3Therapy.fulfilled,
      (state, action: TherapyMutationAction<ITherapy, CreateTreatmentPlanV3TherapyPayload>) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedTherapyPage(state, action)) return;
        state.therapyPage?.therapies.push(action.payload);
      },
    )
    .addCase(createTreatmentPlanV3Therapy.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      if (!isForLoadedTherapyPage(state, action)) return;
      state.error = action.error;
    });
}

function addUpdateTreatmentPlanV3TherapyCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3TherapyThunks,
) {
  const { updateTreatmentPlanV3Therapy } = thunks;
  const flag = 'updateTherapyLoading';

  builder
    .addCase(updateTreatmentPlanV3Therapy.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      updateTreatmentPlanV3Therapy.fulfilled,
      (state, action: TherapyMutationAction<ITherapy, UpdateTreatmentPlanV3TherapyPayload>) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedTherapyPage(state, action)) return;
        const list = state.therapyPage?.therapies;
        if (!list) return;
        const index = list.findIndex((row) => row.id === action.payload.id);
        if (index !== -1) list[index] = action.payload;
      },
    )
    .addCase(updateTreatmentPlanV3Therapy.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      if (!isForLoadedTherapyPage(state, action)) return;
      state.error = action.error;
    });
}

function addDeleteTreatmentPlanV3TherapyCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3TherapyThunks,
) {
  const { deleteTreatmentPlanV3Therapy } = thunks;
  const flag = 'deleteTherapyLoading';

  builder
    .addCase(deleteTreatmentPlanV3Therapy.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      deleteTreatmentPlanV3Therapy.fulfilled,
      (state, action: TherapyMutationAction<void, DeleteTreatmentPlanV3TherapyPayload>) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedTherapyPage(state, action) || !state.therapyPage) return;
        state.therapyPage.therapies = state.therapyPage.therapies.filter(
          (row) => row.id !== action.meta.arg.therapyId,
        );
      },
    )
    .addCase(deleteTreatmentPlanV3Therapy.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      if (!isForLoadedTherapyPage(state, action)) return;
      state.error = action.error;
    });
}

function addSetTreatmentPlanV3NoTherapyCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3TherapyThunks,
) {
  const { setTreatmentPlanV3NoTherapy } = thunks;
  const flag = 'setNoTherapyLoading';

  builder
    .addCase(setTreatmentPlanV3NoTherapy.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      setTreatmentPlanV3NoTherapy.fulfilled,
      (
        state,
        action: TherapyMutationAction<
          ITreatmentPlanV3TherapyPage,
          SetTreatmentPlanV3NoTherapyPayload
        >,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedTherapyPage(state, action)) return;
        state.therapyPage = action.payload;
      },
    )
    .addCase(setTreatmentPlanV3NoTherapy.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      if (!isForLoadedTherapyPage(state, action)) return;
      state.error = action.error;
    });
}
